import { resolveExecutor, getLogger, type GqlExecutor } from '@hopdrive/sdk-core'
import { generateOperationId, scopeLogger, elapsed } from '../../logging'
import { updateLaneStatus } from '../lane-update/updateLaneStatus'
import { logEvent } from '@hopdrive/sdk-events'

export interface RequestLaneEnrichmentParams {
  /** Lane ID to re-enrich */
  laneId: number
  /** Who requested it (email) */
  user: string
  /** Role name (e.g., 'admin', 'dispatcher') */
  role: string
  /** Optional customer ID for event context */
  customerId?: number
  /** Optional extra context stored in event metadata */
  metadata?: unknown
  /** Optional executor override */
  executor?: GqlExecutor
  /** Optional correlation ID for log tracing */
  correlationId?: string
}

export interface RequestLaneEnrichmentResult {
  laneId: number
  status: 'pending'
  eventId: number
}

/**
 * Request lane re-enrichment by setting the lane status back to "pending"
 * and logging an audit trail event.
 *
 * @example
 * ```typescript
 * const result = await requestLaneEnrichment({
 *   laneId: 123,
 *   user: 'admin@example.com',
 *   role: 'admin',
 * })
 * console.log(`Event logged: ${result.eventId}`)
 * ```
 */
export async function requestLaneEnrichment(
  params: RequestLaneEnrichmentParams
): Promise<RequestLaneEnrichmentResult> {
  const { laneId, user, role, customerId, metadata } = params
  const executor = resolveExecutor(params.executor)

  const log = scopeLogger(getLogger(), {
    operationId: generateOperationId(),
    operation: 'requestLaneEnrichment',
    laneId,
    ...(params.correlationId && { correlationId: params.correlationId }),
  })
  const startedAt = Date.now()

  log.info('requestLaneEnrichment.start', { laneId, user, role })

  // Step 1: Set lane status to 'pending'
  await updateLaneStatus({ laneId, status: 'pending', executor })

  // Step 2: Log audit event
  const event = await logEvent('lane.enrichment.requested', {
    user,
    role,
    lane_id: laneId,
    ...(customerId !== undefined && { customer_id: customerId }),
    ...(metadata !== undefined && { metadata }),
    executor,
  })

  log.info('requestLaneEnrichment.complete', {
    laneId,
    eventId: event.id,
    elapsedMs: elapsed(startedAt),
  })

  return {
    laneId,
    status: 'pending',
    eventId: event.id,
  }
}
